import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  Collapse,
  IconButton,
  Link,
  Grid
} from '@mui/material';
import {
  Edit,
  CloudUpload,
  FileUpload,
  LinkedIn,
  PictureAsPdf,
  Description,
  ArrowForward,
  Star,
  Speed,
  Security,
  ExpandMore,
  Help,
  AccountBox,
  Person
} from '@mui/icons-material';
import tokens from '../../theme/tokens';
import { useLanguage } from '../../localization';
import { personaMap, professionToPersonaId } from '../../personas';

const WelcomeScreen = ({ 
  onStartManual, 
  onUploadJson, 
  onUploadLinkedIn, 
  onUploadDocx, 
  onUploadPdf,
  isImporting 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();

  // Simple fade in effect
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handlePersonaSelect = (professionName) => {
    // Use the same redirect approach as the landing page
    const personaParam = professionToPersonaId(professionName);
    const appUrl = `/app?persona=${personaParam}`;
    window.location.href = appUrl;
  };

  const uploadOptions = [
    {
      id: 'linkedin',
      title: t('welcome.importOptions.linkedin.title'),
      description: t('welcome.importOptions.linkedin.description'),
      icon: <LinkedIn />,
      color: tokens.colors.primary.main,
      action: onUploadLinkedIn,
      popular: true,
      badge: t('welcome.importOptions.linkedin.badge')
    },
    {
      id: 'personas',
      title: t('welcome.importOptions.personas.title'),
      description: t('welcome.importOptions.personas.description'),
      icon: <AccountBox />,
      color: tokens.colors.info,
      action: null,
      popular: true,
      badge: t('welcome.importOptions.personas.badge'),
      expandable: true
    },
    {
      id: 'json',
      title: t('welcome.importOptions.json.title'),
      description: t('welcome.importOptions.json.description'),
      icon: <FileUpload />,
      color: tokens.colors.secondary.main,
      action: onUploadJson,
      popular: false
    },
    {
      id: 'docx',
      title: t('welcome.importOptions.docx.title'),
      description: t('welcome.importOptions.docx.description'),
      icon: <Description />,
      color: tokens.colors.text.disabled,
      action: null,
      popular: false,
      disabled: true,
      badge: t('welcome.importOptions.docx.badge')
    },
    {
      id: 'pdf',
      title: t('welcome.importOptions.pdf.title'),
      description: t('welcome.importOptions.pdf.description'),
      icon: <PictureAsPdf />,
      color: '#d32f2f', // Red color for PDF
      action: onUploadPdf,
      popular: true,
      disabled: true,
      badge: t('welcome.importOptions.pdf.badge')
    }
  ];

  const PersonaGrid = () => {
    const professionNames = Object.keys(personaMap);
    
    return (
      <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
        {professionNames.map((profession) => (
          <Grid item xs={6} key={profession}>
            <Button
              variant="text"
              size="small"
              fullWidth
              onClick={() => handlePersonaSelect(profession)}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                py: 0.5,
                px: 1,
                fontSize: '0.7rem',
                fontWeight: 'normal',
                textTransform: 'none',
                color: tokens.colors.text.secondary,
                minHeight: 26,
                borderRadius: tokens.radius.sm,
                '&:hover': {
                  backgroundColor: `${tokens.colors.info}12`,
                  color: tokens.colors.info
                }
              }}
              startIcon={
                <Person sx={{ 
                  fontSize: 12,
                  minWidth: 12
                }} />
              }
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.7rem',
                  lineHeight: 1.1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 'inherit'
                }}
              >
                {profession}
              </Typography>
            </Button>
          </Grid>
        ))}
      </Grid>
    );
  };

  const UploadCard = ({ option }) => {
    const [expanded, setExpanded] = useState(false);
    const isLinkedIn = option.id === 'linkedin';
    const isPersonas = option.id === 'personas';
    const isExpandable = isLinkedIn || isPersonas;
    const isDisabled = option.disabled;

    const handleExpandClick = (e) => {
      e.stopPropagation();
      if (!isDisabled) {
        setExpanded(!expanded);
      }
    };

    const handleCardClick = () => {
      if (!expanded && !isExpandable && option.action && !isDisabled) {
        option.action();
      }
    };

    return (
      <Card
        sx={{
          cursor: isDisabled ? 'not-allowed' : (expanded || isExpandable ? 'default' : 'pointer'),
          transition: 'all 0.3s ease',
          border: `1px solid ${tokens.colors.borders.light}`,
          borderRadius: tokens.radius.xl,
          position: 'relative',
          overflow: 'visible',
          opacity: isDisabled ? 0.6 : 1,
          backgroundColor: isDisabled ? tokens.colors.background.paper : 'inherit',
          '&:hover': isDisabled ? {} : {
            borderColor: option.color,
            boxShadow: tokens.shadows.lg,
            transform: expanded ? 'none' : 'translateY(-2px)'
          }
        }}
        onClick={handleCardClick}
      >
        {(option.popular || option.disabled) && (
          <Chip
            icon={option.disabled ? undefined : <Star sx={{ fontSize: 14 }} />}
            label={option.badge}
            size="small"
            sx={{
              position: 'absolute',
              top: -8,
              right: 12,
              backgroundColor: option.disabled ? '#757575' : option.color,
              color: 'white',
              fontSize: '0.7rem',
              height: 20,
              zIndex: 1,
              '& .MuiChip-icon': {
                color: 'white'
              }
            }}
          />
        )}
        <CardContent sx={{ p: { xs: 2, md: 2.5, lg: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, md: 1.75 } }}>
            <Box
              sx={{
                width: { xs: 40, md: 44 },
                height: { xs: 40, md: 44 },
                borderRadius: '50%',
                backgroundColor: `${option.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              {React.cloneElement(option.icon, {
                sx: { 
                  fontSize: { xs: 20, md: 22 }, 
                  color: isDisabled ? tokens.colors.text.disabled : option.color 
                }
              })}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: tokens.typography.fontWeights.semibold,
                    color: isDisabled ? tokens.colors.text.disabled : tokens.colors.text.primary,
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                    mb: { xs: 0.25, md: 0.25 }
                  }}
                >
                  {option.title}
                </Typography>
                {isExpandable && (
                  <IconButton
                    onClick={handleExpandClick}
                    size="small"
                    sx={{
                      color: tokens.colors.text.secondary,
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      ml: 1
                    }}
                  >
                    <ExpandMore sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: isDisabled ? tokens.colors.text.disabled : tokens.colors.text.secondary,
                  fontSize: { xs: '0.8rem', md: '0.825rem' },
                  lineHeight: 1.4
                }}
              >
                {option.description}
              </Typography>
            </Box>
          </Box>

          {/* LinkedIn Instructions */}
          {isLinkedIn && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: { xs: 2, md: 1.5 }, pt: { xs: 2, md: 1.5 }, borderTop: `1px solid ${tokens.colors.borders.light}` }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: tokens.colors.text.primary,
                    fontWeight: tokens.typography.fontWeights.semibold,
                    fontSize: { xs: '0.9rem', md: '0.85rem' },
                    mb: { xs: 1.5, md: 1.25 }
                  }}
                >
                  {t('welcome.importOptions.linkedin.howToTitle')}
                </Typography>
                <Stack spacing={{ xs: 1, md: 0.75 }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.75rem' }, color: tokens.colors.text.secondary }}>
                    <strong>1.</strong> {t('welcome.importOptions.linkedin.step1')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.75rem' }, color: tokens.colors.text.secondary }}>
                    <strong>2.</strong> {t('welcome.importOptions.linkedin.step2')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.75rem' }, color: tokens.colors.text.secondary }}>
                    <strong>3.</strong> {t('welcome.importOptions.linkedin.step3')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.75rem' }, color: tokens.colors.text.secondary }}>
                    <strong>4.</strong> {t('welcome.importOptions.linkedin.step4')}
                  </Typography>
                </Stack>
                <Box sx={{ mt: { xs: 2, md: 1.5 }, display: 'flex', gap: 1.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      option.action();
                    }}
                    sx={{
                      backgroundColor: option.color,
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      px: 2,
                      py: 0.5,
                      '&:hover': {
                        backgroundColor: tokens.colors.primary.dark
                      }
                    }}
                  >
                    {t('welcome.importOptions.linkedin.uploadButton')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    component={Link}
                    href="https://www.linkedin.com/mypreferences/d/download-my-data"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      borderColor: option.color,
                      color: option.color,
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      px: 2,
                      py: 0.5,
                      '&:hover': {
                        backgroundColor: `${option.color}10`,
                        borderColor: option.color
                      }
                    }}
                  >
                    {t('welcome.importOptions.linkedin.openLinkedIn')}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Persona Templates */}
          {isPersonas && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: { xs: 1.5, md: 1.25 }, pt: { xs: 1.5, md: 1.25 }, borderTop: `1px solid ${tokens.colors.borders.light}` }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: tokens.colors.text.primary,
                    fontWeight: tokens.typography.fontWeights.semibold,
                    fontSize: { xs: '0.85rem', md: '0.8rem' },
                    mb: { xs: 0.75, md: 0.5 }
                  }}
                >
                  Choose a Professional Template
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: tokens.colors.text.secondary,
                    fontSize: { xs: '0.75rem', md: '0.7rem' },
                    mb: { xs: 1, md: 0.75 }
                  }}
                >
                  Select from {Object.keys(personaMap).length} professional CV templates to get started quickly
                </Typography>
                <PersonaGrid />
              </Box>
            </Collapse>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        p: { xs: 2, sm: 3, md: 3, lg: 2.5 },
        maxWidth: '1000px',
        mx: 'auto',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.6s ease-out'
      }}
    >
      {/* Header */}
      <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: tokens.typography.fontWeights.bold,
            color: tokens.colors.text.primary,
            fontSize: { xs: '1.3rem', sm: '1.75rem', md: '1.75rem', lg: '1.9rem' },
            mb: { xs: 1, md: 1.5 },
            lineHeight: 1.2
          }}
        >
          {t('welcome.title')}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: tokens.colors.text.secondary,
            fontSize: { xs: '0.85rem', sm: '1rem', md: '0.95rem' },
            lineHeight: 1.6,
            mb: { xs: 2, md: 2.5 }
          }}
        >
          {t('welcome.subtitle')}
        </Typography>

        {/* Trust indicators */}
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            icon={<Security sx={{ fontSize: { xs: 14, md: 16 } }} />}
            label={t('welcome.trustIndicators.private')}
            size="small"
            sx={{
              backgroundColor: tokens.colors.alpha.white[900],
              border: `1px solid ${tokens.colors.borders.light}`,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              height: { xs: 24, md: 28 }
            }}
          />
          <Chip
            icon={<Speed sx={{ fontSize: { xs: 14, md: 16 } }} />}
            label={t('welcome.trustIndicators.fast')}
            size="small"
            sx={{
              backgroundColor: tokens.colors.alpha.white[900],
              border: `1px solid ${tokens.colors.borders.light}`,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              height: { xs: 24, md: 28 }
            }}
          />
        </Stack>
      </Box>

      {/* Manual Entry Option */}
      <Card
        sx={{
          mb: { xs: 3, md: 3 },
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          border: `2px solid ${tokens.colors.primary.main}`,
          borderRadius: tokens.radius.xl,
          background: `linear-gradient(135deg, ${tokens.colors.primary.main}05, ${tokens.colors.background.default})`,
          '&:hover': {
            boxShadow: tokens.shadows.lg,
            transform: 'translateY(-2px)',
            background: `linear-gradient(135deg, ${tokens.colors.primary.main}10, ${tokens.colors.background.default})`
          }
        }}
        onClick={onStartManual}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3, lg: 2.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 2.5 } }}>
            <Box
              sx={{
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                borderRadius: '50%',
                backgroundColor: tokens.colors.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Edit sx={{ fontSize: { xs: 22, md: 24 }, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: tokens.typography.fontWeights.semibold,
                  color: tokens.colors.text.primary,
                  fontSize: { xs: '1.1rem', md: '1.15rem' },
                  mb: { xs: 0.5, md: 0.75 }
                }}
              >
                {t('welcome.startFromScratch.title')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: tokens.colors.text.secondary,
                  fontSize: { xs: '0.9rem', md: '0.9rem' },
                  lineHeight: 1.5,
                  mb: { xs: 1.5, md: 1.5 }
                }}
              >
                {t('welcome.startFromScratch.description')}
              </Typography>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                sx={{
                  backgroundColor: tokens.colors.primary.main,
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: tokens.typography.fontWeights.medium,
                  px: { xs: 2.5, md: 2.5 },
                  py: { xs: 0.8, md: 0.8 },
                  fontSize: { xs: '0.9rem', md: '0.9rem' },
                  borderRadius: tokens.radius.base,
                  '&:hover': {
                    backgroundColor: tokens.colors.primary.dark
                  }
                }}
              >
                {t('welcome.startFromScratch.getStarted')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Divider */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2.5, md: 2.5 } }}>
        <Divider sx={{ flex: 1 }} />
        <Typography
          variant="body2"
          sx={{
            px: 2,
            color: tokens.colors.text.secondary,
            fontSize: { xs: '0.8rem', md: '0.85rem' },
            fontWeight: tokens.typography.fontWeights.medium
          }}
        >
          {t('welcome.orImport')}
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>

      {/* Import Options */}
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: tokens.typography.fontWeights.semibold,
            color: tokens.colors.text.primary,
            fontSize: { xs: '1rem', md: '1.05rem' },
            mb: { xs: 2, md: 2 }
          }}
        >
          {t('welcome.importOptions.title')}
        </Typography>
        <Stack spacing={{ xs: 1.5, md: 1.5 }}>
          {uploadOptions.map((option) => (
            <UploadCard key={option.id} option={option} />
          ))}
        </Stack>
      </Box>

      {/* Loading State */}
      {isImporting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <CloudUpload sx={{ fontSize: 48, color: tokens.colors.primary.main, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('welcome.processing.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.colors.text.secondary }}>
              {t('welcome.processing.subtitle')}
            </Typography>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default WelcomeScreen; 